import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import axios from 'axios';
import { ArrowLeft, Download, Plus, X, Calendar as CalendarIcon, Users, Phone, User, Mail, CheckCircle2, XCircle, Edit, Check, Search, Filter, DollarSign } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import AdminDashboard from '@/components/AdminDashboard';
import BookingCalendar from '@/components/BookingCalendar';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Simple admin credentials (you should use proper auth in production)
const ADMIN_USERNAME = 'admin';
const ADMIN_PASSWORD = 'whitemeadow2026';

const Admin = () => {
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  // Manual Booking Form States
  const [showManualBookingForm, setShowManualBookingForm] = useState(false);
  const [manualForm, setManualForm] = useState({
    checkin: '',
    checkout: '',
    guests: 1,
    cottages: 1,
    room: 'cottage',
    name: '',
    phone: '',
    email: '',
  });
  const [checkingAvailability, setCheckingAvailability] = useState(false);
  const [availabilityStatus, setAvailabilityStatus] = useState(null);
  const [addingBooking, setAddingBooking] = useState(false);

  // Edit Booking States
  const [showEditForm, setShowEditForm] = useState(false);
  const [editingBooking, setEditingBooking] = useState(null);
  const [updatingBooking, setUpdatingBooking] = useState(false);

  // Filter and Search States
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all'); // all, pending, confirmed, cancelled
  const [dateFilter, setDateFilter] = useState('all'); // all, today, week, month, custom
  const [customDateFrom, setCustomDateFrom] = useState('');
  const [customDateTo, setCustomDateTo] = useState('');
  const [view, setView] = useState('list'); // list, calendar
  
  // Price Edit States
  const [editingPrice, setEditingPrice] = useState(null);
  const [newPrice, setNewPrice] = useState('');

  const roomDetails = {
    cottage: { name: 'Cottage' },
    dormitory: { name: 'Dormitory' },
    full_property: { name: 'Full Property' },
  };

  useEffect(() => {
    // Check if already logged in (simple session storage)
    const loggedIn = sessionStorage.getItem('adminLoggedIn');
    if (loggedIn === 'true') {
      setIsAuthenticated(true);
      fetchBookings();
    }
  }, []);

  const handleLogin = (e) => {
    e.preventDefault();
    if (username === ADMIN_USERNAME && password === ADMIN_PASSWORD) {
      setIsAuthenticated(true);
      sessionStorage.setItem('adminLoggedIn', 'true');
      setError('');
      fetchBookings();
    } else {
      setError('Invalid credentials');
    }
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    sessionStorage.removeItem('adminLoggedIn');
    setUsername('');
    setPassword('');
  };

  const fetchBookings = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/bookings`);
      setBookings(response.data);
    } catch (error) {
      console.error('Error fetching bookings:', error);
      setError('Failed to load bookings');
    } finally {
      setLoading(false);
    }
  };

  const exportToCSV = () => {
    const headers = ['ID', 'Name', 'Phone', 'Email', 'Room', 'Check-in', 'Check-out', 'Guests', 'Total', 'Booked At'];
    const rows = bookings.map(b => [
      b.id,
      b.name,
      b.phone,
      b.email || '',
      roomDetails[b.room]?.name || b.room,
      new Date(b.checkin).toLocaleDateString(),
      new Date(b.checkout).toLocaleDateString(),
      b.guests,
      b.total,
      new Date(b.created_at).toLocaleString()
    ]);

    const csvContent = [headers, ...rows].map(row => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `white-meadow-bookings-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  // Pricing Logic (same as BookingApp)
  const getPricing = (room, isWeekend, guests, cottages = 1) => {
    const prices = {
      cottage: { weekend: 4999, weekday: 3999, maxGuests: null, overflowRate: 0 },
      dormitory: { weekend: 19999, weekday: 17999, maxGuests: 16, overflowRate: 1200 },
      full_property: { weekend: 29999, weekday: 27999, maxGuests: 25, overflowRate: 1200 },
    };

    const roomPricing = prices[room];
    const basePrice = isWeekend ? roomPricing.weekend : roomPricing.weekday;
    
    let overflowCharge = 0;
    if (roomPricing.maxGuests && guests > roomPricing.maxGuests) {
      const extraGuests = guests - roomPricing.maxGuests;
      overflowCharge = extraGuests * roomPricing.overflowRate;
    }

    // For cottages, multiply the total by number of cottages
    const singleRoomPrice = basePrice + overflowCharge;
    if (room === 'cottage') {
      return singleRoomPrice * cottages;
    }

    return singleRoomPrice;
  };

  const isWeekend = (date) => {
    const day = new Date(date).getDay();
    return day === 5 || day === 6;
  };

  const calculateTotal = () => {
    if (!manualForm.checkin || !manualForm.checkout) return 0;

    const checkinDate = new Date(manualForm.checkin);
    const checkoutDate = new Date(manualForm.checkout);
    const nights = Math.ceil((checkoutDate - checkinDate) / (1000 * 60 * 60 * 24));

    if (nights <= 0) return 0;

    let total = 0;
    for (let i = 0; i < nights; i++) {
      const currentDate = new Date(checkinDate);
      currentDate.setDate(currentDate.getDate() + i);
      const nightPrice = getPricing(manualForm.room, isWeekend(currentDate), manualForm.guests, manualForm.cottages);
      total += nightPrice;
    }

    return total;
  };

  const nights = manualForm.checkin && manualForm.checkout
    ? Math.max(0, Math.ceil((new Date(manualForm.checkout) - new Date(manualForm.checkin)) / (1000 * 60 * 60 * 24)))
    : 0;

  const total = calculateTotal();

  const handleManualFormChange = (e) => {
    const { name, value } = e.target;
    setManualForm({ ...manualForm, [name]: value });
    setAvailabilityStatus(null);
  };

  const checkAvailability = async () => {
    if (!manualForm.checkin || !manualForm.checkout) {
      alert('Please select check-in and check-out dates');
      return;
    }

    setCheckingAvailability(true);
    try {
      const response = await axios.post(`${API}/bookings/check-availability`, {
        checkin: new Date(manualForm.checkin).toISOString(),
        checkout: new Date(manualForm.checkout).toISOString(),
        room: manualForm.room,
        cottages: parseInt(manualForm.cottages) || 1,
      });

      setAvailabilityStatus(response.data);
    } catch (error) {
      console.error('Error checking availability:', error);
      setAvailabilityStatus({
        available: false,
        message: 'Error checking availability. Please try again.',
      });
    } finally {
      setCheckingAvailability(false);
    }
  };

  const addManualBooking = async () => {
    // Validation
    if (!manualForm.checkin || !manualForm.checkout || !manualForm.name || !manualForm.phone) {
      alert('Please fill in all required fields');
      return;
    }

    if (nights <= 0) {
      alert('Check-out date must be after check-in date');
      return;
    }

    if (!availabilityStatus?.available) {
      alert('Please check availability first');
      return;
    }

    setAddingBooking(true);

    try {
      const bookingData = {
        checkin: new Date(manualForm.checkin).toISOString(),
        checkout: new Date(manualForm.checkout).toISOString(),
        guests: parseInt(manualForm.guests),
        cottages: parseInt(manualForm.cottages),
        room: manualForm.room,
        name: manualForm.name,
        phone: manualForm.phone,
        email: manualForm.email || null,
      };

      await axios.post(`${API}/bookings`, bookingData);

      // Refresh bookings list
      await fetchBookings();

      // Reset form and close modal
      setManualForm({
        checkin: '',
        checkout: '',
        guests: 1,
        cottages: 1,
        room: 'cottage',
        name: '',
        phone: '',
        email: '',
      });
      setAvailabilityStatus(null);
      setShowManualBookingForm(false);

      alert('✅ Manual booking added successfully!');
    } catch (error) {
      console.error('Error adding booking:', error);
      alert(error.response?.data?.detail || 'Failed to add booking. Please try again.');
    } finally {
      setAddingBooking(false);
    }
  };

  // Open edit modal with booking data
  const openEditForm = (booking) => {
    setEditingBooking(booking);
    setManualForm({
      checkin: booking.checkin.split('T')[0],
      checkout: booking.checkout.split('T')[0],
      guests: booking.guests,
      cottages: booking.cottages || 1,
      room: booking.room,
      name: booking.name,
      phone: booking.phone,
      email: booking.email || '',
    });
    // Set availability as available by default for existing bookings
    setAvailabilityStatus({ available: true, message: 'Editing existing booking' });
    setShowEditForm(true);
  };

  // Update existing booking
  const updateBooking = async () => {
    if (!manualForm.checkin || !manualForm.checkout || !manualForm.name || !manualForm.phone) {
      alert('Please fill in all required fields');
      return;
    }

    if (nights <= 0) {
      alert('Check-out date must be after check-in date');
      return;
    }

    setUpdatingBooking(true);

    try {
      const updateData = {
        checkin: new Date(manualForm.checkin).toISOString(),
        checkout: new Date(manualForm.checkout).toISOString(),
        guests: parseInt(manualForm.guests),
        cottages: parseInt(manualForm.cottages) || 1,
        room: manualForm.room,
        name: manualForm.name,
        phone: manualForm.phone,
        email: manualForm.email || null,
        total: total, // Include recalculated total
      };

      await axios.put(`${API}/bookings/${editingBooking.id}`, updateData);

      await fetchBookings();

      setManualForm({
        checkin: '',
        checkout: '',
        guests: 1,
        cottages: 1,
        room: 'cottage',
        name: '',
        phone: '',
        email: '',
      });
      setAvailabilityStatus(null);
      setShowEditForm(false);
      setEditingBooking(null);

      alert('✅ Booking updated successfully!');
    } catch (error) {
      console.error('Error updating booking:', error);
      alert(error.response?.data?.detail || 'Failed to update booking. Please try again.');
    } finally {
      setUpdatingBooking(false);
    }
  };

  // Confirm booking
  const confirmBooking = async (bookingId) => {
    if (!window.confirm('Confirm this booking?')) return;

    try {
      await axios.patch(`${API}/bookings/${bookingId}/status`, { status: 'confirmed' });
      await fetchBookings();
      alert('✅ Booking confirmed!');
    } catch (error) {
      console.error('Error confirming booking:', error);
      alert('Failed to confirm booking');
    }
  };

  // Cancel booking
  const cancelBooking = async (bookingId) => {
    if (!window.confirm('Cancel this booking? This action cannot be undone.')) return;

    try {
      await axios.patch(`${API}/bookings/${bookingId}/status`, { status: 'cancelled' });
      await fetchBookings();
      alert('✅ Booking cancelled');
    } catch (error) {
      console.error('Error cancelling booking:', error);
      alert('Failed to cancel booking');
    }
  };

  // Update price
  const updatePrice = async (bookingId) => {
    if (!newPrice || isNaN(newPrice) || parseFloat(newPrice) <= 0) {
      alert('Please enter a valid price');
      return;
    }

    try {
      await axios.put(`${API}/bookings/${bookingId}`, { total: parseFloat(newPrice) });
      await fetchBookings();
      setEditingPrice(null);
      setNewPrice('');
      alert('✅ Price updated successfully!');
    } catch (error) {
      console.error('Error updating price:', error);
      alert('Failed to update price');
    }
  };

  // Filter and search bookings
  const getFilteredBookings = () => {
    let filtered = [...bookings];

    // Status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(b => b.status === statusFilter);
    }

    // Date filter
    if (dateFilter !== 'all') {
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      
      if (dateFilter === 'today') {
        filtered = filtered.filter(b => {
          const checkin = new Date(b.checkin);
          const checkout = new Date(b.checkout);
          return checkin <= today && checkout > today;
        });
      } else if (dateFilter === 'week') {
        const weekFromNow = new Date(today.getTime() + 7 * 24 * 60 * 60 * 1000);
        filtered = filtered.filter(b => {
          const checkin = new Date(b.checkin);
          return checkin >= today && checkin <= weekFromNow;
        });
      } else if (dateFilter === 'month') {
        const monthFromNow = new Date(today.getTime() + 30 * 24 * 60 * 60 * 1000);
        filtered = filtered.filter(b => {
          const checkin = new Date(b.checkin);
          return checkin >= today && checkin <= monthFromNow;
        });
      } else if (dateFilter === 'custom') {
        // Custom date range filter
        if (customDateFrom || customDateTo) {
          filtered = filtered.filter(b => {
            const checkin = new Date(b.checkin);
            const checkout = new Date(b.checkout);
            
            let matchesFrom = true;
            let matchesTo = true;
            
            if (customDateFrom) {
              const fromDate = new Date(customDateFrom);
              matchesFrom = checkout >= fromDate;
            }
            
            if (customDateTo) {
              const toDate = new Date(customDateTo);
              toDate.setHours(23, 59, 59); // Include the entire day
              matchesTo = checkin <= toDate;
            }
            
            return matchesFrom && matchesTo;
          });
        }
      }
    }

    // Search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(b => 
        b.name.toLowerCase().includes(query) ||
        b.phone.includes(query) ||
        (b.email && b.email.toLowerCase().includes(query)) ||
        b.room.toLowerCase().includes(query)
      );
    }

    return filtered;
  };

  const filteredBookings = getFilteredBookings();

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#F5E6D3] via-[#E8D4B8] to-[#D4B896] flex items-center justify-center px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-md"
        >
          <Card className="shadow-2xl border-2 border-[#3D2817]/20">
            <CardContent className="p-8">
              <div className="text-center mb-8">
                <img src="/logo.jpeg" alt="White Meadow" className="h-20 w-20 rounded-full object-cover mx-auto mb-4" />
                <h1 className="text-3xl font-bold text-[#3D2817] mb-2">Admin Login</h1>
                <p className="text-sm text-[#5C4633]">White Meadow Resort</p>
              </div>

              <form onSubmit={handleLogin} className="space-y-6">
                <div>
                  <Label htmlFor="username" className="text-[#3D2817] mb-2 block">Username</Label>
                  <Input
                    type="text"
                    id="username"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    className="border-[#3D2817]/30"
                    placeholder="Enter username"
                    data-testid="admin-username"
                  />
                </div>

                <div>
                  <Label htmlFor="password" className="text-[#3D2817] mb-2 block">Password</Label>
                  <Input
                    type="password"
                    id="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="border-[#3D2817]/30"
                    placeholder="Enter password"
                    data-testid="admin-password"
                  />
                </div>

                {error && (
                  <p className="text-sm text-red-600 text-center">{error}</p>
                )}

                <Button
                  type="submit"
                  className="w-full bg-[#2D5016] hover:bg-[#223E10] text-white py-3 rounded-none transition-all duration-300 font-medium"
                  data-testid="admin-login-btn"
                >
                  Login
                </Button>
              </form>

              <div className="mt-6 text-center">
                <button
                  onClick={() => navigate('/')}
                  className="text-sm text-[#2D5016] hover:underline flex items-center justify-center gap-2 mx-auto"
                >
                  <ArrowLeft className="w-4 h-4" />
                  Back to Website
                </button>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#F5E6D3] via-[#E8D4B8] to-[#D4B896]">
      {/* Header */}
      <header className="bg-[#3D2817] text-white py-6 shadow-lg">
        <div className="container mx-auto px-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img src="/logo.jpeg" alt="White Meadow" className="h-12 w-12 rounded-full object-cover" />
            <div>
              <h1 className="text-2xl font-bold">Admin Dashboard</h1>
              <p className="text-sm text-[#D4B896]">White Meadow Resort</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <Button
              onClick={() => navigate('/admin/settings')}
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-none transition-all duration-300 font-medium flex items-center gap-2"
            >
              <Edit className="w-4 h-4" />
              Settings
            </Button>
            <Button
              onClick={exportToCSV}
              className="bg-[#D4B896] text-[#3D2817] hover:bg-[#C5A87D] px-4 py-2 rounded-none transition-all duration-300 font-medium flex items-center gap-2"
              data-testid="export-csv-btn"
            >
              <Download className="w-4 h-4" />
              Export CSV
            </Button>
            <Button
              onClick={handleLogout}
              className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-none transition-all duration-300 font-medium"
              data-testid="admin-logout-btn"
            >
              Logout
            </Button>
          </div>
        </div>
      </header>

      {/* Dashboard Stats */}
      <div className="container mx-auto px-4 py-12">
        <AdminDashboard bookings={bookings} />

        {/* Calendar/List View Toggle */}
        <div className="flex justify-end mb-6">
          <div className="inline-flex rounded-lg border border-[#3D2817]/20 overflow-hidden">
            <button
              onClick={() => setView('list')}
              className={`px-6 py-2 font-medium transition ${
                view === 'list'
                  ? 'bg-[#3D2817] text-white'
                  : 'bg-white text-[#3D2817] hover:bg-gray-100'
              }`}
            >
              List View
            </button>
            <button
              onClick={() => setView('calendar')}
              className={`px-6 py-2 font-medium transition ${
                view === 'calendar'
                  ? 'bg-[#3D2817] text-white'
                  : 'bg-white text-[#3D2817] hover:bg-gray-100'
              }`}
            >
              Calendar View
            </button>
          </div>
        </div>

        {/* Calendar View */}
        {view === 'calendar' && (
          <BookingCalendar 
            bookings={bookings}
            onDateClick={(date) => {
              console.log('Date clicked:', date);
              setView('list');
            }}
          />
        )}

        {/* List View */}
        {view === 'list' && (
          <Card className="shadow-2xl border-2 border-[#3D2817]/20">
            <CardContent className="p-8">
              {/* Header with Buttons */}
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-3xl font-bold text-[#3D2817]">
                  All Bookings ({filteredBookings.length})
                </h2>
                <div className="flex gap-3">
                  <Button
                    onClick={() => setShowManualBookingForm(true)}
                    className="bg-[#2D5016] hover:bg-[#223E10] text-white px-6 py-2 rounded-none transition-all duration-300 font-medium flex items-center gap-2"
                    data-testid="add-manual-booking-btn"
                  >
                    <Plus className="w-5 h-5" />
                    Add Manual Booking
                  </Button>
                  <Button
                    onClick={fetchBookings}
                    className="bg-[#3D2817] hover:bg-[#2D1F0F] text-white px-6 py-2 rounded-none transition-all duration-300 font-medium"
                    data-testid="refresh-bookings-btn"
                  >
                    Refresh
                  </Button>
                  <Button
                    onClick={exportToCSV}
                    className="bg-[#2D5016] hover:bg-[#223E10] text-white px-6 py-2 rounded-none transition-all duration-300 font-medium flex items-center gap-2"
                  >
                    <Download className="w-5 h-5" />
                    Export CSV
                  </Button>
                </div>
              </div>

              {/* Search and Filters */}
              <div className="grid grid-cols-1 md:grid-cols-6 gap-4 mb-6 p-4 bg-gray-50 rounded-lg">
                {/* Search */}
                <div className="md:col-span-3">
                  <Label className="text-sm font-semibold text-[#3D2817] mb-2 flex items-center gap-2">
                    <Search className="w-4 h-4" />
                    Search Bookings
                  </Label>
                  <Input
                    type="text"
                    placeholder="Search by name, phone, email, room..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="border-[#3D2817]/30"
                  />
                </div>

                {/* Status Filter */}
                <div className="md:col-span-1">
                  <Label className="text-sm font-semibold text-[#3D2817] mb-2 flex items-center gap-2">
                    <Filter className="w-4 h-4" />
                    Status
                  </Label>
                  <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    className="w-full px-3 py-2 border border-[#3D2817]/30 rounded-md focus:outline-none focus:ring-2 focus:ring-[#3D2817]"
                  >
                    <option value="all">All Status</option>
                    <option value="pending">Pending</option>
                    <option value="confirmed">Confirmed</option>
                    <option value="cancelled">Cancelled</option>
                  </select>
                </div>

                {/* Date Filter */}
                <div className="md:col-span-2">
                  <Label className="text-sm font-semibold text-[#3D2817] mb-2 flex items-center gap-2">
                    <CalendarIcon className="w-4 h-4" />
                    Date Range
                  </Label>
                  <div className="flex gap-2">
                    <select
                      value={dateFilter}
                      onChange={(e) => {
                        setDateFilter(e.target.value);
                        if (e.target.value !== 'custom') {
                          setCustomDateFrom('');
                          setCustomDateTo('');
                        }
                      }}
                      className="flex-1 px-3 py-2 border border-[#3D2817]/30 rounded-md focus:outline-none focus:ring-2 focus:ring-[#3D2817]"
                    >
                      <option value="all">All Dates</option>
                      <option value="today">Today</option>
                      <option value="week">This Week</option>
                      <option value="month">This Month</option>
                      <option value="custom">Custom Range</option>
                    </select>
                  </div>
                  
                  {/* Custom Date Range Inputs */}
                  {dateFilter === 'custom' && (
                    <div className="grid grid-cols-2 gap-2 mt-2">
                      <div>
                        <Label htmlFor="custom-date-from" className="text-xs text-gray-600">From Date</Label>
                        <Input
                          type="date"
                          id="custom-date-from"
                          value={customDateFrom}
                          onChange={(e) => setCustomDateFrom(e.target.value)}
                          className="border-[#3D2817]/30 text-sm"
                          placeholder="From"
                        />
                      </div>
                      <div>
                        <Label htmlFor="custom-date-to" className="text-xs text-gray-600">To Date</Label>
                        <Input
                          type="date"
                          id="custom-date-to"
                          value={customDateTo}
                          onChange={(e) => setCustomDateTo(e.target.value)}
                          className="border-[#3D2817]/30 text-sm"
                          placeholder="To"
                          min={customDateFrom}
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {loading ? (
                <p className="text-center text-gray-500 py-8">Loading bookings...</p>
              ) : filteredBookings.length === 0 ? (
                <p className="text-center text-gray-500 py-8">
                  {searchQuery || statusFilter !== 'all' || dateFilter !== 'all'
                    ? 'No bookings match your filters'
                    : 'No bookings yet'}
                </p>
              ) : (
                <div className="space-y-4">
                  {filteredBookings.map((booking, i) => (
                  <motion.div
                    key={booking.id}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: i * 0.05 }}
                  >
                    <Card className="bg-gradient-to-r from-[#FAFAFA] to-[#F5E6D3] border-[#3D2817]/30">
                      <CardContent className="p-6">
                        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                          <div>
                            <div className="flex items-center gap-2 mb-2">
                              <h3 className="font-bold text-lg text-[#3D2817]">{booking.name}</h3>
                              <span className={`text-xs px-2 py-1 rounded-full font-semibold ${
                                booking.status === 'confirmed' ? 'bg-green-100 text-green-800' :
                                booking.status === 'cancelled' ? 'bg-red-100 text-red-800' :
                                'bg-yellow-100 text-yellow-800'
                              }`}>
                                {booking.status === 'confirmed' ? '✓ Confirmed' :
                                 booking.status === 'cancelled' ? '✗ Cancelled' :
                                 '⏳ Pending'}
                              </span>
                            </div>
                            <p className="text-sm text-gray-700">📱 {booking.phone}</p>
                            {booking.email && <p className="text-sm text-gray-700">📧 {booking.email}</p>}
                            <p className="text-xs text-gray-500 mt-2">
                              Booked: {new Date(booking.created_at).toLocaleString()}
                            </p>
                          </div>
                          <div>
                            <p className="text-sm font-semibold text-[#3D2817]">
                              🏠 {roomDetails[booking.room]?.name || booking.room}
                            </p>
                            <p className="text-sm text-gray-700">👥 {booking.guests} guests</p>
                          </div>
                          <div>
                            <p className="text-sm text-gray-700">
                              📅 Check-in: {new Date(booking.checkin).toLocaleDateString()}
                            </p>
                            <p className="text-sm text-gray-700">
                              📅 Check-out: {new Date(booking.checkout).toLocaleDateString()}
                            </p>
                          </div>
                          <div className="text-right">
                            {editingPrice === booking.id ? (
                              <div className="space-y-2">
                                <Input
                                  type="number"
                                  value={newPrice}
                                  onChange={(e) => setNewPrice(e.target.value)}
                                  placeholder="Enter new price"
                                  className="text-right border-[#3D2817]/30"
                                  autoFocus
                                />
                                <div className="flex gap-1">
                                  <Button
                                    onClick={() => updatePrice(booking.id)}
                                    className="flex-1 bg-green-600 hover:bg-green-700 text-white text-xs px-2 py-1"
                                  >
                                    Save
                                  </Button>
                                  <Button
                                    onClick={() => {
                                      setEditingPrice(null);
                                      setNewPrice('');
                                    }}
                                    className="flex-1 bg-gray-400 hover:bg-gray-500 text-white text-xs px-2 py-1"
                                  >
                                    Cancel
                                  </Button>
                                </div>
                              </div>
                            ) : (
                              <>
                                <div className="flex items-center justify-end gap-2">
                                  <p className="text-2xl font-bold text-[#2D5016]">
                                    ₹{booking.total.toLocaleString('en-IN')}
                                  </p>
                                  {booking.status !== 'cancelled' && (
                                    <button
                                      onClick={() => {
                                        setEditingPrice(booking.id);
                                        setNewPrice(booking.total.toString());
                                      }}
                                      className="text-xs bg-orange-600 hover:bg-orange-700 text-white px-2 py-1 rounded transition"
                                      title="Edit price"
                                    >
                                      Edit Cost
                                    </button>
                                  )}
                                </div>
                                <p className="text-xs text-gray-600">
                                  {Math.ceil((new Date(booking.checkout) - new Date(booking.checkin)) / (1000 * 60 * 60 * 24))} nights
                                </p>
                              </>
                            )}
                          </div>
                          <div className="flex flex-col gap-2">
                            {booking.status !== 'cancelled' && (
                              <>
                                <Button
                                  onClick={() => openEditForm(booking)}
                                  className="bg-blue-600 hover:bg-blue-700 text-white text-xs px-3 py-1 rounded-none flex items-center gap-1"
                                  data-testid={`edit-booking-${booking.id}`}
                                >
                                  <Edit className="w-3 h-3" />
                                  Edit
                                </Button>
                                {booking.status === 'pending' && (
                                  <Button
                                    onClick={() => confirmBooking(booking.id)}
                                    className="bg-green-600 hover:bg-green-700 text-white text-xs px-3 py-1 rounded-none flex items-center gap-1"
                                    data-testid={`confirm-booking-${booking.id}`}
                                  >
                                    <Check className="w-3 h-3" />
                                    Confirm
                                  </Button>
                                )}
                                <Button
                                  onClick={() => cancelBooking(booking.id)}
                                  className="bg-red-600 hover:bg-red-700 text-white text-xs px-3 py-1 rounded-none"
                                  data-testid={`cancel-booking-${booking.id}`}
                                >
                                  Cancel
                                </Button>
                              </>
                            )}
                            {booking.status === 'cancelled' && (
                              <span className="text-xs text-gray-500 italic">Cancelled</span>
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
        )}
      </div>

      <div className="container mx-auto px-4">
        <div className="mt-8 text-center">
          <button
            onClick={() => navigate('/')}
            className="text-[#2D5016] hover:underline flex items-center justify-center gap-2 mx-auto text-lg"
          >
            <ArrowLeft className="w-5 h-5" />
            Back to Website
          </button>
        </div>
      </div>

      {/* Manual Booking Form Modal */}
      <AnimatePresence>
        {showManualBookingForm && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center px-4"
            onClick={() => setShowManualBookingForm(false)}
          >
            <motion.div
              initial={{ scale: 0.9, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, y: 20 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-lg shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto"
            >
              <div className="sticky top-0 bg-[#3D2817] text-white p-6 flex items-center justify-between">
                <h3 className="text-2xl font-bold">Add Manual Booking</h3>
                <button
                  onClick={() => setShowManualBookingForm(false)}
                  className="hover:bg-white/20 p-2 rounded-full transition"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              <div className="p-8">
                {/* Room Selection */}
                <div className="mb-6">
                  <Label className="text-lg font-semibold text-[#3D2817] mb-3 block">
                    Room Type *
                  </Label>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {Object.entries(roomDetails).map(([key, room]) => (
                      <Card
                        key={key}
                        className={`cursor-pointer transition-all ${
                          manualForm.room === key
                            ? 'bg-[#3D2817] text-white border-[#3D2817]'
                            : 'bg-white hover:shadow-md border-gray-200'
                        }`}
                        onClick={() => setManualForm({ ...manualForm, room: key })}
                      >
                        <CardContent className="p-4 text-center">
                          <h4 className="font-bold">{room.name}</h4>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>

                {/* Date Selection */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                  <div>
                    <Label htmlFor="manual-checkin" className="flex items-center gap-2 text-[#3D2817] mb-2">
                      <CalendarIcon className="w-4 h-4" />
                      Check-in Date *
                    </Label>
                    <Input
                      type="date"
                      id="manual-checkin"
                      name="checkin"
                      value={manualForm.checkin}
                      onChange={handleManualFormChange}
                      min={new Date().toISOString().split('T')[0]}
                      className="border-[#3D2817]/30"
                      data-testid="manual-checkin-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="manual-checkout" className="flex items-center gap-2 text-[#3D2817] mb-2">
                      <CalendarIcon className="w-4 h-4" />
                      Check-out Date *
                    </Label>
                    <Input
                      type="date"
                      id="manual-checkout"
                      name="checkout"
                      value={manualForm.checkout}
                      onChange={handleManualFormChange}
                      min={manualForm.checkin || new Date().toISOString().split('T')[0]}
                      className="border-[#3D2817]/30"
                      data-testid="manual-checkout-input"
                    />
                  </div>
                </div>

                {/* Guest Count */}
                <div className="mb-6">
                  <Label htmlFor="manual-guests" className="flex items-center gap-2 text-[#3D2817] mb-2">
                    <Users className="w-4 h-4" />
                    Number of Guests *
                  </Label>
                  <Input
                    type="number"
                    id="manual-guests"
                    name="guests"
                    value={manualForm.guests}
                    onChange={handleManualFormChange}
                    min="1"
                    max="50"
                    className="border-[#3D2817]/30"
                    data-testid="manual-guests-input"
                  />
                </div>

                {/* Number of Cottages (only for cottage bookings) */}
                {manualForm.room === 'cottage' && (
                  <div className="mb-6">
                    <Label htmlFor="manual-cottages" className="flex items-center gap-2 text-[#3D2817] mb-2">
                      <Users className="w-4 h-4" />
                      Number of Cottages (Optional)
                    </Label>
                    <Input
                      type="number"
                      id="manual-cottages"
                      name="cottages"
                      value={manualForm.cottages}
                      onChange={handleManualFormChange}
                      min="1"
                      max="3"
                      className="border-[#3D2817]/30"
                      data-testid="manual-cottages-input"
                    />
                    <p className="text-xs text-gray-600 mt-1">
                      We have 3 cottages available. Each cottage can accommodate guests.
                    </p>
                  </div>
                )}

                {/* Availability Check */}
                {manualForm.checkin && manualForm.checkout && (
                  <div className="mb-6">
                    <Button
                      onClick={checkAvailability}
                      disabled={checkingAvailability}
                      className="w-full bg-[#3D2817] hover:bg-[#2D1F0F] text-white"
                      data-testid="manual-check-availability-btn"
                    >
                      {checkingAvailability ? 'Checking...' : 'Check Availability'}
                    </Button>

                    {availabilityStatus && (
                      <motion.div
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        className={`mt-3 p-4 rounded-lg flex items-center gap-2 ${
                          availabilityStatus.available
                            ? 'bg-green-100 text-green-800'
                            : 'bg-red-100 text-red-800'
                        }`}
                      >
                        {availabilityStatus.available ? (
                          <CheckCircle2 className="w-5 h-5" />
                        ) : (
                          <XCircle className="w-5 h-5" />
                        )}
                        <span className="font-semibold">{availabilityStatus.message}</span>
                      </motion.div>
                    )}
                  </div>
                )}

                {/* Pricing Summary */}
                {nights > 0 && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="bg-[#3D2817] text-white p-6 rounded-lg mb-6"
                  >
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-lg">Nights:</span>
                      <span className="text-2xl font-bold">{nights}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-lg">Total Amount:</span>
                      <span className="text-3xl font-bold">₹{total.toLocaleString('en-IN')}</span>
                    </div>
                  </motion.div>
                )}

                {/* Guest Details */}
                <div className="space-y-4 mb-6">
                  <div>
                    <Label htmlFor="manual-name" className="flex items-center gap-2 text-[#3D2817] mb-2">
                      <User className="w-4 h-4" />
                      Guest Name *
                    </Label>
                    <Input
                      type="text"
                      id="manual-name"
                      name="name"
                      value={manualForm.name}
                      onChange={handleManualFormChange}
                      placeholder="Enter guest name"
                      className="border-[#3D2817]/30"
                      data-testid="manual-name-input"
                    />
                  </div>

                  <div>
                    <Label htmlFor="manual-phone" className="flex items-center gap-2 text-[#3D2817] mb-2">
                      <Phone className="w-4 h-4" />
                      Phone Number *
                    </Label>
                    <Input
                      type="tel"
                      id="manual-phone"
                      name="phone"
                      value={manualForm.phone}
                      onChange={handleManualFormChange}
                      placeholder="+91 XXXXX XXXXX"
                      className="border-[#3D2817]/30"
                      data-testid="manual-phone-input"
                    />
                  </div>

                  <div>
                    <Label htmlFor="manual-email" className="flex items-center gap-2 text-[#3D2817] mb-2">
                      <Mail className="w-4 h-4" />
                      Email (Optional)
                    </Label>
                    <Input
                      type="email"
                      id="manual-email"
                      name="email"
                      value={manualForm.email}
                      onChange={handleManualFormChange}
                      placeholder="guest@email.com"
                      className="border-[#3D2817]/30"
                      data-testid="manual-email-input"
                    />
                  </div>
                </div>

                {/* Add Booking Button */}
                <div className="flex gap-4">
                  <Button
                    onClick={() => setShowManualBookingForm(false)}
                    className="flex-1 bg-gray-300 hover:bg-gray-400 text-gray-800 py-3 rounded-none transition-all duration-300 font-medium"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={addManualBooking}
                    disabled={addingBooking || !availabilityStatus?.available}
                    className="flex-1 bg-[#2D5016] hover:bg-[#223E10] text-white py-3 rounded-none transition-all duration-300 font-medium disabled:opacity-50"
                    data-testid="manual-add-booking-btn"
                  >
                    {addingBooking ? 'Adding...' : 'Add Booking'}
                  </Button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Edit Booking Form Modal */}
      <AnimatePresence>
        {showEditForm && editingBooking && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center px-4"
            onClick={() => setShowEditForm(false)}
          >
            <motion.div
              initial={{ scale: 0.9, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, y: 20 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-lg shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto"
            >
              <div className="sticky top-0 bg-blue-600 text-white p-6 flex items-center justify-between">
                <h3 className="text-2xl font-bold">Edit Booking</h3>
                <button
                  onClick={() => setShowEditForm(false)}
                  className="hover:bg-white/20 p-2 rounded-full transition"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              <div className="p-8">
                {/* Same form fields as manual booking */}
                <div className="mb-6">
                  <Label className="text-lg font-semibold text-[#3D2817] mb-3 block">
                    Room Type *
                  </Label>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {Object.entries(roomDetails).map(([key, room]) => (
                      <Card
                        key={key}
                        className={`cursor-pointer transition-all ${
                          manualForm.room === key
                            ? 'bg-blue-600 text-white border-blue-600'
                            : 'bg-white hover:shadow-md border-gray-200'
                        }`}
                        onClick={() => setManualForm({ ...manualForm, room: key })}
                      >
                        <CardContent className="p-4 text-center">
                          <h4 className="font-bold">{room.name}</h4>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                  <div>
                    <Label htmlFor="edit-checkin" className="flex items-center gap-2 text-[#3D2817] mb-2">
                      <CalendarIcon className="w-4 h-4" />
                      Check-in Date *
                    </Label>
                    <Input
                      type="date"
                      id="edit-checkin"
                      name="checkin"
                      value={manualForm.checkin}
                      onChange={handleManualFormChange}
                      className="border-[#3D2817]/30"
                    />
                  </div>
                  <div>
                    <Label htmlFor="edit-checkout" className="flex items-center gap-2 text-[#3D2817] mb-2">
                      <CalendarIcon className="w-4 h-4" />
                      Check-out Date *
                    </Label>
                    <Input
                      type="date"
                      id="edit-checkout"
                      name="checkout"
                      value={manualForm.checkout}
                      onChange={handleManualFormChange}
                      className="border-[#3D2817]/30"
                    />
                  </div>
                </div>

                <div className="mb-6">
                  <Label htmlFor="edit-guests" className="flex items-center gap-2 text-[#3D2817] mb-2">
                    <Users className="w-4 h-4" />
                    Number of Guests *
                  </Label>
                  <Input
                    type="number"
                    id="edit-guests"
                    name="guests"
                    value={manualForm.guests}
                    onChange={handleManualFormChange}
                    min="1"
                    max="50"
                    className="border-[#3D2817]/30"
                  />
                </div>

                {/* Number of Cottages (only for cottage bookings) */}
                {manualForm.room === 'cottage' && (
                  <div className="mb-6">
                    <Label htmlFor="edit-cottages" className="flex items-center gap-2 text-[#3D2817] mb-2">
                      <Users className="w-4 h-4" />
                      Number of Cottages (Optional)
                    </Label>
                    <Input
                      type="number"
                      id="edit-cottages"
                      name="cottages"
                      value={manualForm.cottages}
                      onChange={handleManualFormChange}
                      min="1"
                      max="3"
                      className="border-[#3D2817]/30"
                    />
                    <p className="text-xs text-gray-600 mt-1">
                      We have 3 cottages available. Each cottage can accommodate guests.
                    </p>
                  </div>
                )}

                {manualForm.checkin && manualForm.checkout && (
                  <div className="mb-6">
                    <Button
                      onClick={checkAvailability}
                      disabled={checkingAvailability}
                      className="w-full bg-[#3D2817] hover:bg-[#2D1F0F] text-white"
                    >
                      {checkingAvailability ? 'Checking...' : 'Check Availability'}
                    </Button>

                    {availabilityStatus && (
                      <motion.div
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        className={`mt-3 p-4 rounded-lg flex items-center gap-2 ${
                          availabilityStatus.available
                            ? 'bg-green-100 text-green-800'
                            : 'bg-red-100 text-red-800'
                        }`}
                      >
                        {availabilityStatus.available ? (
                          <CheckCircle2 className="w-5 h-5" />
                        ) : (
                          <XCircle className="w-5 h-5" />
                        )}
                        <span className="font-semibold">{availabilityStatus.message}</span>
                      </motion.div>
                    )}
                  </div>
                )}

                {nights > 0 && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="bg-blue-600 text-white p-6 rounded-lg mb-6"
                  >
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-lg">Nights:</span>
                      <span className="text-2xl font-bold">{nights}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-lg">Total Amount:</span>
                      <span className="text-3xl font-bold">₹{total.toLocaleString('en-IN')}</span>
                    </div>
                  </motion.div>
                )}

                <div className="space-y-4 mb-6">
                  <div>
                    <Label htmlFor="edit-name" className="flex items-center gap-2 text-[#3D2817] mb-2">
                      <User className="w-4 h-4" />
                      Guest Name *
                    </Label>
                    <Input
                      type="text"
                      id="edit-name"
                      name="name"
                      value={manualForm.name}
                      onChange={handleManualFormChange}
                      placeholder="Enter guest name"
                      className="border-[#3D2817]/30"
                    />
                  </div>

                  <div>
                    <Label htmlFor="edit-phone" className="flex items-center gap-2 text-[#3D2817] mb-2">
                      <Phone className="w-4 h-4" />
                      Phone Number *
                    </Label>
                    <Input
                      type="tel"
                      id="edit-phone"
                      name="phone"
                      value={manualForm.phone}
                      onChange={handleManualFormChange}
                      placeholder="+91 XXXXX XXXXX"
                      className="border-[#3D2817]/30"
                    />
                  </div>

                  <div>
                    <Label htmlFor="edit-email" className="flex items-center gap-2 text-[#3D2817] mb-2">
                      <Mail className="w-4 h-4" />
                      Email (Optional)
                    </Label>
                    <Input
                      type="email"
                      id="edit-email"
                      name="email"
                      value={manualForm.email}
                      onChange={handleManualFormChange}
                      placeholder="guest@email.com"
                      className="border-[#3D2817]/30"
                    />
                  </div>
                </div>

                <div className="flex gap-4">
                  <Button
                    onClick={() => setShowEditForm(false)}
                    className="flex-1 bg-gray-300 hover:bg-gray-400 text-gray-800 py-3 rounded-none transition-all duration-300 font-medium"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={updateBooking}
                    disabled={updatingBooking}
                    className="flex-1 bg-blue-600 hover:bg-blue-700 text-white py-3 rounded-none transition-all duration-300 font-medium disabled:opacity-50"
                  >
                    {updatingBooking ? 'Updating...' : 'Update Booking'}
                  </Button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Admin;
